import { titleCase } from '../titleCase.ts';
import { link } from './apply.ts';
import { builderEndsRefuse, endRefusal, parseNeed, wouldLoopBuilder, type BuilderTree } from './builder.ts';
import { BUILDER_END, BUILDER_START } from './ends.ts';
import type { ProjectProcess } from './graph.ts';
import { PHASE_KEYS, type PhaseKey } from './phases.ts';

/**
 * A step's lines, one step at a time (design study 31, slice 5; §5, A): what it needs and what it opens, the lines a
 * longer way already gives (§0.1), why a line cannot be drawn while choosing one, and the walk Next step in the tree
 * takes. Pure: the master's card and a job's setup panel read the same rules.
 */

/** A step's title in the process tree page's quotes. */
const q = (title: string) => `“${titleCase(title)}”`;

/**
 * Each step's direct needs that a longer way already gives (study 31, §0.1): the board leaves them out of its drawing
 * (core `directNeeds`), so its card says so, with the way, the steps between in order from the need towards the step
 * (the shortest such way; ties by the needs' order). `needs` is each step's needs as stored: a key, or a decision's key
 * and one of its options (`key:option`). An option's line is never marked, as the board always draws it, though a
 * longer way through one counts, as the board reads it. A step with none is left out.
 */
export function impliedLines(needs: ReadonlyMap<string, readonly string[]>): Map<string, { need: string; through: string[] }[]> {
  const keysOf = new Map([...needs].map(([k, ns]) => [k, [...new Set(ns.map((d) => parseNeed(d).key))].filter((d) => d !== k)]));
  const out = new Map<string, { need: string; through: string[] }[]>();
  for (const [step, ns] of needs) {
    const lines: { need: string; through: string[] }[] = [];
    const plain = [...new Set(ns.filter((d) => parseNeed(d).option === null))].filter((d) => d !== step);
    for (const need of plain) {
      const through = longerWay(keysOf, step, need);
      if (through) lines.push({ need, through });
    }
    if (lines.length) out.set(step, lines);
  }
  return out;
}

/** The shortest way from `need` to `step` but the direct line, as the steps between in order; null when there is none. */
function longerWay(keysOf: ReadonlyMap<string, readonly string[]>, step: string, need: string): string[] | null {
  // Back from the step, breadth first, so the first way found is the shortest, and of those the one whose needs come first.
  const towards = new Map<string, string>();
  const queue: string[] = [];
  const seen = new Set([step, need]);
  for (const d of keysOf.get(step) ?? []) {
    if (seen.has(d)) continue;
    seen.add(d);
    towards.set(d, step);
    queue.push(d);
  }
  for (let i = 0; i < queue.length; i++) {
    const at = queue[i]!;
    for (const d of keysOf.get(at) ?? []) {
      if (d === need) {
        const way: string[] = [];
        for (let k = at; k !== step; k = towards.get(k)!) way.push(k);
        return way;
      }
      if (seen.has(d)) continue;
      seen.add(d);
      towards.set(d, at);
      queue.push(d);
    }
  }
  return null;
}

/**
 * Why `from` cannot wait on `to` on the master, in words, or null when the line can be drawn: the ends (nothing before
 * the start, nothing after the end), itself, a task of its own or its own step, a loop, or `to` in a later phase than `from` (study 31,
 * slice 5: greyed while choosing; `builderProblem` does not refuse it, so a tree already holding one still saves).
 */
export function builderLinkRefusal(t: Pick<BuilderTree, 'nodes' | 'phases'>, from: string, to: string): string | null {
  const byKey = new Map(t.nodes.map((n) => [n.key, n]));
  const a = byKey.get(from);
  const b = byKey.get(to);
  if (!a || !b) return 'Both steps must be in the tree.';
  const ends = builderEndsRefuse(t, from, to);
  if (ends) return ends;
  if (from === to) return 'A step cannot wait on itself.';
  /** Whether `x` is a task of `y`, however deep in its lists: either way round they are in order already, as on a job. */
  const inside = (x: string, y: string) => {
    const seen = new Set<string>();
    for (let up = byKey.get(x)?.parent ?? null; up !== null && !seen.has(up); up = byKey.get(up)?.parent ?? null) {
      if (up === y) return true;
      seen.add(up);
    }
    return false;
  };
  if (inside(to, from)) return `${q(b.title)} is a task of ${q(a.title)}.`;
  if (inside(from, to)) return `${q(a.title)} is a task of ${q(b.title)}.`;
  if (wouldLoopBuilder(t, from, to)) return `${q(b.title)} already waits on ${q(a.title)}, all the way back: that would loop.`;
  const at = (k: string | null) => (k === null ? -1 : t.phases.findIndex((p) => p.key === k));
  if (at(a.phase) >= 0 && at(b.phase) > at(a.phase)) {
    return `${q(b.title)} is in ${t.phases[at(b.phase)]!.name}, after ${t.phases[at(a.phase)]!.name}: a step waits only on one in its phase or before.`;
  }
  return null;
}

/**
 * Why nothing can wait on `to` on a job, in words, or null: Project Complete is the end, and nothing comes after it, in
 * the master's words (`builderEndsRefuse`), whether or not a line onto it would loop (its own waits taken out, nothing
 * may lead to it). The API's `linkSteps` refuses it in the same words.
 */
export function jobEndRefusal(p: ProjectProcess, to: string): string | null {
  const end = p.steps.find((s) => s.id === to && !s.off);
  return end?.key === BUILDER_END && end.instance === null ? endRefusal(end.title) : null;
}

/**
 * Why `from` cannot wait on `to` on a job, in words, or null when the line can be drawn: the start (it waits on
 * nothing, as the API's `linkSteps` refuses), the end (nothing waits on it: `jobEndRefusal`), then what core `link`
 * refuses, in its words (itself, one step inside the other, a loop), read with the job's `phases` (core
 * `projectPhases`) as `link` reads them: a task done early is not after its step's start (study 31, slice 4).
 */
export function jobLinkRefusal(p: ProjectProcess, from: string, to: string, phases?: ReadonlyMap<string, PhaseKey>): string | null {
  const start = p.steps.find((s) => s.id === from && !s.off);
  if (start?.key === BUILDER_START && start.instance === null) return `${titleCase(start.title)} is where the job begins: it waits on nothing.`;
  const end = jobEndRefusal(p, to);
  if (end) return end;
  try {
    link(p, from, to, phases);
    return null;
  } catch (e) {
    return e instanceof Error ? e.message : String(e);
  }
}

/**
 * The order Next step in the tree walks the steps in (study 31, slice 5): the start, then phase by phase in `phases`'
 * order (the five, unless the master's own are given), each phase in the order of its waits (what a step needs in its
 * phase before it), ties by `order`; then the steps in no phase of those, the same way, then the end. Every step once.
 */
export function walkOrder(
  steps: readonly { key: string; phase: string | null; needs: readonly string[]; end?: 'start' | 'end' }[],
  order: readonly string[],
  phases: readonly string[] = PHASE_KEYS,
): string[] {
  const unique = [...new Map(steps.map((s) => [s.key, s])).values()];
  const at = new Map(order.map((k, i) => [k, i]));
  const given = new Map(unique.map((s, i) => [s.key, i]));
  const rank = (k: string) => at.get(k) ?? order.length + given.get(k)!;
  const groupOf = (s: (typeof unique)[number]): number => {
    if (s.end === 'start') return -1;
    if (s.end === 'end') return phases.length + 1;
    const i = s.phase === null ? -1 : phases.indexOf(s.phase);
    return i < 0 ? phases.length : i;
  };
  const groups = new Map<number, typeof unique>();
  for (const s of unique) groups.set(groupOf(s), [...(groups.get(groupOf(s)) ?? []), s]);
  const walk: string[] = [];
  for (const g of [...groups.keys()].sort((x, y) => x - y)) {
    const inGroup = groups.get(g)!;
    const here = new Set(inGroup.map((s) => s.key));
    const waits = new Map(inGroup.map((s) => [s.key, new Set(s.needs.map((d) => parseNeed(d).key).filter((d) => d !== s.key && here.has(d)))]));
    const left = new Set(here);
    while (left.size) {
      const ready = [...left].filter((k) => [...waits.get(k)!].every((d) => !left.has(d)));
      // A loop (the tree refuses one) walks on in the order given.
      const next = (ready.length ? ready : [...left]).reduce((x, y) => (rank(y) < rank(x) ? y : x));
      walk.push(next);
      left.delete(next);
    }
  }
  return walk;
}
